import { Component, inject, input, signal, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PaymentDetail, PaymentListItem } from '../../core/models';

// Status → PrimeNG tag severity. Semantic colour that reads at a glance in the list.
const STATUS_SEVERITY: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
    Captured: 'success',
    Authorized: 'info',
    Authorizing: 'info',
    Created: 'secondary',
    Failed: 'danger',
    Voided: 'warn',
    Refunded: 'warn',
    PartiallyRefunded: 'warn'
};

// Per-location payments tab embedded in the merchant detail page. Same list+detail as the global
// /payments page (reuses getPayments/getPaymentDetail), but the merchant is FIXED by the parent via
// the locationId signal input — so there's no merchant selector, and every fetch is scoped to it.
@Component({
    selector: 'app-payments-tab',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule, SelectModule, SelectButtonModule, InputTextModule, TooltipModule, FormsModule, DecimalPipe, DatePipe],
    template: `
        <div class="flex flex-wrap items-center gap-2 mb-3">
            <p-selectbutton [options]="modeOptions" [(ngModel)]="mode" (onChange)="reload()" optionLabel="label" optionValue="value" [allowEmpty]="false" />
            <p-select [options]="statusOptions()" [(ngModel)]="status" (onChange)="reload()" placeholder="All statuses" [showClear]="true" styleClass="w-48" />
            <input pInputText [(ngModel)]="search" (keyup.enter)="reload()" placeholder="Search reference / order / card last4" class="w-72" />
            <p-button icon="pi pi-search" label="Search" size="small" (onClick)="reload()" />
            <p-button icon="pi pi-refresh" [text]="true" size="small" (onClick)="reload()" />
        </div>

        <p-table [value]="items()" [loading]="loading()" [lazy]="true" [paginator]="true" [rows]="pageSize"
                 [totalRecords]="total()" (onLazyLoad)="onPage($event)" [first]="first()">
            <ng-template pTemplate="header">
                <tr>
                    <th>Status</th><th>Amount</th><th>Method</th>
                    <th>Card</th><th>Reference</th><th>Created</th><th></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-p>
                <tr class="cursor-pointer" (click)="open(p)">
                    <td class="whitespace-nowrap"><p-tag [value]="p.status" [severity]="sev(p.status)" />
                        @if (p.isTest) { <p-tag value="TEST" severity="warn" styleClass="ml-1" /> }
                    </td>
                    <td class="font-medium tabular-nums">{{ p.currency }} {{ p.amount | number: '1.2-2' }}
                        @if (p.refundedAmount > 0) { <span class="text-surface-400 text-xs">(−{{ p.refundedAmount | number: '1.2-2' }})</span> }
                    </td>
                    <td>{{ p.methodCode }}</td>
                    <td>@if (p.cardLast4) { {{ p.cardBrand }} •••• {{ p.cardLast4 }} } @else { <span class="text-surface-400">—</span> }</td>
                    <td class="text-sm">{{ p.clientReference }}@if (p.clientOrderReference) { <span class="text-surface-400"> · {{ p.clientOrderReference }}</span> }</td>
                    <td class="text-sm text-surface-500">{{ p.createdAt | date: 'short' }}</td>
                    <td><p-button icon="pi pi-eye" [text]="true" size="small" (onClick)="open(p)" /></td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr><td colspan="7">{{ error() ?? 'No payments for this merchant yet.' }}</td></tr>
            </ng-template>
        </p-table>

        <p-dialog [(visible)]="showDetail" [modal]="true" [style]="{ width: '52rem' }" [dismissableMask]="true"
                  [header]="'Payment ' + (detail()?.intentId ?? '')">
            @if (detailLoading()) {
                <p class="text-sm text-surface-500">Loading…</p>
            } @else if (detail(); as d) {
                <div class="flex items-center gap-2 mb-4">
                    <p-tag [value]="d.status" [severity]="sev(d.status)" />
                    @if (d.isTest) { <p-tag value="TEST" severity="warn" /> }
                    <span class="text-lg font-semibold tabular-nums">{{ d.currency }} {{ d.amount | number: '1.2-2' }}</span>
                    @if (d.cardLast4) { <span class="text-surface-500">· {{ d.cardBrand }} ending {{ d.cardLast4 }}</span> }
                </div>
                @if (d.failureMessage) {
                    <p-message severity="error" styleClass="mb-3">{{ d.failureMessage }}</p-message>
                }

                <div class="grid grid-cols-2 gap-2 mb-4 text-sm">
                    <div><b>Merchant:</b> {{ d.merchantName }} ({{ d.locationId }})</div>
                    <div><b>Method:</b> {{ d.methodCode }}</div>
                    <div><b>Client reference:</b> {{ d.clientReference }}</div>
                    <div><b>Order reference:</b> {{ d.clientOrderReference ?? '—' }}</div>
                    <div><b>Authorized:</b> {{ d.authorizedAmount | number: '1.2-2' }}</div>
                    <div><b>Captured:</b> {{ d.capturedAmount | number: '1.2-2' }}</div>
                    <div><b>Refunded:</b> {{ d.refundedAmount | number: '1.2-2' }}</div>
                    <div><b>Created:</b> {{ d.createdAt | date: 'medium' }}</div>
                    <div class="col-span-2"><b>Gateway order:</b> {{ d.gatewayOrderId ?? '—' }}
                        @if (d.authorizationId) { · <b>auth:</b> {{ d.authorizationId }} }
                        @if (d.captureId) { · <b>capture:</b> {{ d.captureId }} }
                    </div>
                </div>

                <h6>Operation trail</h6>
                <p-table [value]="d.operations" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>Step</th><th>Result</th><th>Attempt</th><th>Gateway ref</th><th>Code</th><th>When</th><th></th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-o let-i="rowIndex">
                        <tr>
                            <td>{{ o.type }}</td>
                            <td><p-tag [value]="o.status" [severity]="o.status === 'Succeeded' ? 'success' : (o.status === 'Failed' ? 'danger' : 'secondary')" /></td>
                            <td>{{ o.attemptNumber }}</td>
                            <td class="text-sm">{{ o.gatewayReference ?? '—' }}</td>
                            <td class="text-sm">{{ o.gatewayRespCode ?? (o.errorCategory ?? '—') }}</td>
                            <td class="text-sm text-surface-500">{{ o.at | date: 'medium' }}</td>
                            <td>
                                @if (o.rawRequest || o.rawResponse) {
                                    <p-button [icon]="expandedOp() === i ? 'pi pi-chevron-up' : 'pi pi-code'" [text]="true" size="small" (onClick)="toggleOp(i)" />
                                }
                            </td>
                        </tr>
                        <!-- Kept IDENTICAL to pages/payments/payments.ts on purpose: this tab is a second,
                             independent copy of the same viewer over the same PaymentDetail and the same
                             getPaymentDetail call. Changing one and not the other leaves the merchant-scoped
                             screen silently showing half the exchange with nothing to say why. -->
                        @if ((o.rawRequest || o.rawResponse) && expandedOp() === i) {
                            <tr>
                                <td colspan="7">
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <div class="text-xs font-semibold mb-1">Sent<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Card token, cardholder name and expiry are never stored, so they are not shown." tooltipPosition="top"></i></div>
                                            @if (o.rawRequest) {
                                                <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">{{ pretty(o.rawRequest) }}</pre>
                                            } @else {
                                                <p class="text-xs text-surface-500 dark:text-surface-400">Not recorded for this operation.</p>
                                            }
                                        </div>
                                        <div>
                                            <div class="text-xs font-semibold mb-1">Received<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The processor's response, shortened and with sensitive values removed." tooltipPosition="top"></i></div>
                                            @if (o.rawResponse) {
                                                <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64">{{ pretty(o.rawResponse) }}</pre>
                                            } @else {
                                                <p class="text-xs text-surface-500 dark:text-surface-400">Not recorded for this operation.</p>
                                            }
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="7">No operations recorded.</td></tr></ng-template>
                </p-table>
            }
        </p-dialog>
    `
})
export class PaymentsTab implements OnInit {
    private readonly api = inject(MiddlewareApi);

    // Location is fixed by the parent (merchant detail). Signal input — read as this.locationId().
    readonly locationId = input.required<string>();

    readonly items = signal<PaymentListItem[]>([]);
    readonly total = signal(0);
    readonly first = signal(0);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly statusOptions = signal<{ label: string; value: string }[]>([]);
    readonly pageSize = 25;

    search = '';
    status: string | null = null;
    mode = 'all';   // all | live | test
    readonly modeOptions = [
        { label: 'All', value: 'all' },
        { label: 'Live', value: 'live' },
        { label: 'Test', value: 'test' }
    ];
    readonly expandedOp = signal<number | null>(null);

    readonly showDetail = signal(false);
    readonly detail = signal<PaymentDetail | null>(null);
    readonly detailLoading = signal(false);

    ngOnInit(): void {
        this.api.getPaymentStatuses().subscribe({
            next: (s) => this.statusOptions.set((s ?? []).map((v) => ({ label: v, value: v }))),
            error: () => {}
        });
        // NOTE: no load() here — the [lazy] p-table fires onLazyLoad -> onPage on first render, which
        // owns the initial fetch (and reads this.locationId() at that point). Calling load(1) too would
        // double the first request.
    }

    toggleOp(i: number): void {
        this.expandedOp.set(this.expandedOp() === i ? null : i);
    }

    pretty(raw: string): string {
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
    }

    sev(status: string) {
        return STATUS_SEVERITY[status] ?? 'secondary';
    }

    onPage(e: { first?: number | null; rows?: number | null }): void {
        const first = e.first ?? 0;
        this.first.set(first);
        this.load(Math.floor(first / this.pageSize) + 1);
    }

    reload(): void {
        this.first.set(0);
        this.load(1);
    }

    private load(page: number): void {
        this.loading.set(true);
        this.error.set(null);
        this.api.getPayments({ locationId: this.locationId(), page, pageSize: this.pageSize, search: this.search || undefined, status: this.status || undefined, mode: this.mode !== 'all' ? this.mode : undefined }).subscribe({
            next: (r) => {
                this.items.set(r.items ?? []);
                this.total.set(r.total ?? 0);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    open(p: PaymentListItem): void {
        this.showDetail.set(true);
        this.expandedOp.set(null);
        this.detail.set(null);
        this.detailLoading.set(true);
        this.api.getPaymentDetail(p.intentId).subscribe({
            next: (d) => { this.detail.set(d); this.detailLoading.set(false); },
            error: () => { this.detailLoading.set(false); this.showDetail.set(false); }
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
